// Mobile-optimized Supabase client for Capacitor
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { CapacitorHttp, Capacitor } from '@capacitor/core';
import { logger } from '@/utils/logger';

const SUPABASE_URL = "https://vxdhvklxtvwtgtnkznrl.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZ4ZGh2a2x4dHZ3dGd0bmt6bnJsIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDQ0MDI2MTksImV4cCI6MjA1OTk3ODYxOX0.M13tzUuJJtHA9xaOVJbfl4xGPvS-bVECIXm3lbVENRM";

// XMLHttpRequest fallback function for iOS
const xmlHttpRequestFallback = (url: string, options: RequestInit = {}): Promise<Response> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const method = options.method || 'GET';

    logger.log('[Mobile Client] Creating XMLHttpRequest');

    xhr.onreadystatechange = function() {
      logger.log(`[Mobile Client] XHR readyState changed to: ${xhr.readyState}`);
      if (xhr.readyState === 4) {
        logger.log(`[Mobile Client] XHR completed with status: ${xhr.status}`);
        
        // Handle the completion in readyState change since onload might not fire
        if (xhr.status === undefined || xhr.status === 0) {
          logger.error('[Mobile Client] XHR status undefined in readyState 4 - rejecting');
          reject(new Error('Network request failed - CORS or network restriction'));
          return;
        }

        // If we have a valid status, process the response
        logger.log(`[Mobile Client] XHR successful response: ${xhr.responseText?.substring(0, 300)}`);
        
        const responseHeaders = new Headers();
        const headerString = xhr.getAllResponseHeaders();
        const headerPairs = headerString.split('\r\n');
        
        headerPairs.forEach(header => {
          const parts = header.split(': ');
          if (parts.length === 2) {
            responseHeaders.append(parts[0], parts[1]);
          }
        });
        
        resolve(new Response(xhr.responseText, {
          status: xhr.status,
          headers: responseHeaders,
        }));
      }
    };
    
    xhr.open(method, url, true);
    logger.log(`[Mobile Client] XHR opened: ${method} ${url}`);

    // Log request details for debugging
    logger.log(`[Mobile Client] XHR Request Details:`, {
      method,
      url,
      headers: options.headers,
      body: options.body ? options.body.toString().substring(0, 200) : 'no body'
    });
    
    // Set headers
    if (options.headers) {
      if (options.headers instanceof Headers) {
        options.headers.forEach((value, key) => {
          logger.log(`[Mobile Client] Setting header: ${key} = ${value}`);
          xhr.setRequestHeader(key, value);
        });
      } else {
        Object.entries(options.headers).forEach(([key, value]) => {
          if (typeof value === 'string') {
            logger.log(`[Mobile Client] Setting header: ${key} = ${value}`);
            xhr.setRequestHeader(key, value);
          }
        });
      }
    }

    // Set content type for POST requests with JSON body
    if (method === 'POST' && options.body && !options.headers?.['Content-Type']) {
      logger.log(`[Mobile Client] Setting default Content-Type: application/json`);
      xhr.setRequestHeader('Content-Type', 'application/json');
    }
    
    // Handle response
    xhr.onloadstart = function() {
      logger.log('[Mobile Client] XHR load started');
    };

    xhr.onprogress = function(e) {
      logger.log(`[Mobile Client] XHR progress: ${e.loaded}/${e.total}`);
    };

    xhr.onerror = function() {
      logger.error('[Mobile Client] XHR Error - network failure or CORS issue');
      reject(new Error('Network request failed'));
    };

    xhr.ontimeout = function() {
      logger.error('[Mobile Client] XHR Timeout after 30 seconds');
      reject(new Error('Request timeout'));
    };
    
    // Set timeout
    xhr.timeout = 30000;
    
    // Send the request
    if (options.body) {
      // Convert body to string if it's not already
      let bodyToSend;
      if (typeof options.body === 'string') {
        bodyToSend = options.body;
      } else if (options.body instanceof FormData || options.body instanceof Blob || options.body instanceof ArrayBuffer) {
        bodyToSend = options.body;
      } else {
        // For other types like ReadableStream, convert to string
        bodyToSend = JSON.stringify(options.body);
      }
      logger.log(`[Mobile Client] Sending body: ${typeof bodyToSend === 'string' ? bodyToSend.substring(0, 200) : 'binary data'}`);
      xhr.send(bodyToSend);
    } else {
      logger.log(`[Mobile Client] Sending request with no body`);
      xhr.send();
    }
  });
};

// Create a custom fetch implementation using Capacitor HTTP plugin
const customFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  try {
    const method = options.method || 'GET';
    
    // If we're in a web browser, use regular fetch
    if (Capacitor.getPlatform() === 'web') {
      logger.log('[Mobile Client] Using regular fetch for web platform');
      return fetch(url, options);
    }

    // Check if we should use XMLHttpRequest fallback for iOS
    const isIOS = Capacitor.getPlatform() === 'ios';

    logger.log(`[Mobile Client] Request to: ${url}`, { method });
    logger.log(`[Mobile Client] Platform: ${Capacitor.getPlatform()}, isIOS: ${isIOS}`);

    if (isIOS) {
      logger.log('[Mobile Client] UPDATED VERSION - Using XMLHttpRequest fallback for ALL requests in iOS');
      return xmlHttpRequestFallback(url, options);
    }
    
    // For non-iOS platforms, use CapacitorHttp
    // Convert headers to a simple object
    const headers: Record<string, string> = {};
    
    if (options.headers) {
      if (options.headers instanceof Headers) {
        options.headers.forEach((value, key) => {
          headers[key] = value;
        });
      } else if (Array.isArray(options.headers)) {
        options.headers.forEach(([key, value]) => {
          headers[key] = value;
        });
      } else {
        Object.entries(options.headers).forEach(([key, value]) => {
          if (typeof value === 'string') {
            headers[key] = value;
          }
        });
      }
    }

    // Process the request body
    let data = options.body;
    
    // If body is a string, try to parse it as JSON if it looks like JSON
    if (typeof options.body === 'string' &&
        (options.body.startsWith('{') || options.body.startsWith('['))) {
      try {
        data = JSON.parse(options.body);
        logger.log('[Mobile Client] Successfully parsed body as JSON');
      } catch (e) {
        // If parsing fails, keep it as a string
        logger.log('[Mobile Client] Could not parse body as JSON, using as string');
      }
    }

    // Prepare the request options for CapacitorHttp
    const requestOptions = {
      url,
      method,
      headers,
      data: data,
      // Add these options to improve reliability
      connectTimeout: 30000, // 30 seconds
      readTimeout: 30000,    // 30 seconds
      shouldFollowRedirect: true,
    };

    logger.log('[Mobile Client] Making request with options:', {
      url,
      method,
      headerKeys: Object.keys(headers),
      hasData: !!data
    });

    // Make the request using Capacitor HTTP plugin
    const response = await CapacitorHttp.request(requestOptions);

    logger.log(`[Mobile Client] Response status: ${response.status}`);
    
    // Create a Response object from the Capacitor HTTP response
    const responseData = response.data;
    let responseBody;
    
    if (typeof responseData === 'object') {
      responseBody = JSON.stringify(responseData);
      logger.log('[Mobile Client] Response data keys:', Object.keys(responseData));
    } else {
      responseBody = responseData || '{}';
      logger.log('[Mobile Client] Response is not an object');
    }
    
    // Create headers object from response
    const responseHeaders = new Headers();
    if (response.headers) {
      Object.entries(response.headers).forEach(([key, value]) => {
        if (typeof value === 'string') {
          responseHeaders.append(key, value);
        }
      });
    }
    
    return new Response(responseBody, {
      status: response.status,
      headers: responseHeaders,
    });
  } catch (error) {
    logger.error('[Mobile Client] Error:', error);

    // Return a proper error response instead of throwing
    return new Response(JSON.stringify({
      error: 'Network request failed',
      details: error instanceof Error ? error.message : String(error)
    }), {
      status: 500,
      headers: new Headers({ 'Content-Type': 'application/json' }),
    });
  }
};

// Override global fetch for iOS to ensure ALL requests use XMLHttpRequest
if (typeof window !== 'undefined' && Capacitor.getPlatform() === 'ios') {
  logger.log('[Mobile Client] Overriding global fetch for iOS');
  (window as any).fetch = customFetch;
}

// Create the Supabase client with the custom fetch implementation
export const mobileSupabase = createClient<Database>(
  SUPABASE_URL, 
  SUPABASE_PUBLISHABLE_KEY,
  {
    global: {
      fetch: customFetch,
    },
    auth: {
      persistSession: true,
      detectSessionInUrl: false,
      autoRefreshToken: true,
      flowType: 'pkce',
    },
  }
);
